// NeoCard — the dashboard's neomorphic (soft-UI) surface. Raised cards get
// dual-source shadows tuned to the cream canvas; `inset` renders a pressed
// well (gauges, timelines, icon chips). Interactive cards mirror GlassCard's
// keyboard semantics: Enter/Space fire the click handler, role=button,
// focus ring. Presentational cards stay out of the tab order.

import { ReactNode } from 'react';
import { motion } from 'framer-motion';

type NeoCardProps = {
  children: ReactNode;
  className?: string;
  variant?: 'raised' | 'inset';
  /** Raised cards lift + glow on hover when interactive or hover=true. */
  hover?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
};

const NeoCard = ({
  children,
  className = '',
  variant = 'raised',
  hover = false,
  onClick,
  ariaLabel,
}: NeoCardProps): JSX.Element => {
  const interactive = !!onClick;
  const surface =
    variant === 'inset' ? 'neo-inset' : interactive || hover ? 'neo-raised-hover' : 'neo-raised';
  return (
    <motion.div
      className={`${surface} ${className} ${interactive ? 'focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40' : ''}`}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={ariaLabel}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
};

export default NeoCard;
